import { useCallback, useEffect, useMemo, useRef } from "react";
import { ParamType, type InputParam } from "../../../types/grid.ts";
import type { AdvancedFilterProps } from "./types.ts";
import { usePublishSiblingCheckbox } from "./filterSiblings.ts";

/**
 * FilterShowCheckBox / FilterHideCheckbox / FilterCheckBox — a single
 * checkbox that maps to an equality filter on a column. Port of the
 * WebAdmin2 CheckboxFilter behaviour.
 *
 * FilterSettings (pipe-separated, case-insensitive) customise what
 * value is emitted:
 *   TRUEONCHECKED      / FALSEONCHECKED      / EMPTYONCHECKED / NULLONCHECKED
 *   TRUEONNOTCHECKED   / FALSEONNOTCHECKED   / EMPTYONNOTCHECKED / NULLONNOTCHECKED
 *   DEFAULTCHECKED     / DEFAULTNOTCHECKED
 *
 * When the chosen state maps to undefined, the filter emits no params
 * (i.e. it is disabled), otherwise it emits a single InputParam with
 * Type=Specified (parameters) or Type=Equal (regular columns).
 *
 * Default semantics mirror the legacy defaults:
 *   FilterShowCheckBox:  onChecked=undefined, onNotChecked=true
 *                        (unchecked ⇒ filter to col=true, i.e. "hide X")
 *   FilterHideCheckbox:  onChecked=true,      onNotChecked=undefined
 *                        (checked   ⇒ filter to col=true, i.e. "hide X")
 *   FilterCheckBox:      onChecked=true,      onNotChecked=false
 *                        (plain equality checkbox)
 */

type Tri = boolean | undefined;

interface Config {
  onChecked: Tri;
  onNotChecked: Tri;
  initialChecked: boolean;
}

function defaultsForType(filterType: string): Config {
  const t = filterType.toUpperCase();
  if (t === "FILTERHIDECHECKBOX") {
    return { onChecked: true, onNotChecked: undefined, initialChecked: false };
  }
  if (t === "FILTERCHECKBOX") {
    return { onChecked: true, onNotChecked: false, initialChecked: false };
  }
  // FilterShowCheckBox and unknown variants.
  return { onChecked: undefined, onNotChecked: true, initialChecked: false };
}

function applySettings(base: Config, raw: string | undefined): Config {
  if (!raw) return base;
  const out: Config = { ...base };
  for (const part of raw.split("|")) {
    switch (part.trim().toUpperCase()) {
      case "FALSEONCHECKED":
        out.onChecked = false;
        break;
      case "TRUEONCHECKED":
        out.onChecked = true;
        break;
      case "EMPTYONCHECKED":
      case "NULLONCHECKED":
        out.onChecked = undefined;
        break;
      case "FALSEONNOTCHECKED":
        out.onNotChecked = false;
        break;
      case "TRUEONNOTCHECKED":
        out.onNotChecked = true;
        break;
      case "EMPTYONNOTCHECKED":
      case "NULLONNOTCHECKED":
        out.onNotChecked = undefined;
        break;
      case "DEFAULTCHECKED":
        out.initialChecked = true;
        break;
      case "DEFAULTNOTCHECKED":
        out.initialChecked = false;
        break;
    }
  }
  return out;
}

/**
 * Always emit a param — even when the configured side is `undefined` ("no
 * filter"). Without this, a checked FilterShowCheckBox emits nothing, the URL
 * loses the user's intent, and after a search/remount the box reverts to its
 * default-unchecked state and silently re-applies the test filter.
 *
 * Sentinel scheme: when the configured value is `undefined`, we still emit a
 * param carrying the raw checkbox boolean BUT with `Type = ParamType.Unused`
 * so the server pipeline drops it (DataGrid.vb:165 `Continue For`). That keeps
 * the round-trip lossless for the UI without altering server semantics.
 */
function buildParams(
  checked: boolean,
  config: Config,
  columnName: string,
  paramType: ParamType,
): InputParam[] {
  const value = checked ? config.onChecked : config.onNotChecked;
  if (value === undefined) {
    return [{ sColumn: columnName, oValue: checked, Type: ParamType.Unused }];
  }
  return [{ sColumn: columnName, oValue: value, Type: paramType }];
}

/** Read the checkbox state encoded in a previously-emitted param. Returns
 *  `undefined` when the param doesn't match either expected mapping (caller
 *  falls back to `config.initialChecked`). */
function decodeChecked(p: InputParam | undefined, config: Config): boolean | undefined {
  if (!p) return undefined;
  // Unused-typed param = our sentinel carrying the raw boolean.
  if (p.Type === ParamType.Unused) return p.oValue === true;
  if (p.oValue === config.onChecked) return true;
  if (p.oValue === config.onNotChecked) return false;
  return undefined;
}

export function ShowCheckboxFilter({
  info,
  isParameter,
  value,
  onChange,
}: AdvancedFilterProps) {
  const config = useMemo(
    () => applySettings(defaultsForType(info.FilterType), info.FilterSettings),
    [info.FilterType, info.FilterSettings],
  );

  const paramType = isParameter ? ParamType.Specified : ParamType.Equal;

  // Fully controlled: derive `checked` from `value` every render. No local
  // useState — the URL/state is the single source of truth, so there's no way
  // for the UI to fall out of sync with the param regardless of effect ordering
  // (URL-load vs emit-default), StrictMode double-mount, or remounts via
  // `loadSignal`/`clearBump` key bumps. Each click flows through onChange →
  // parent filterState → value prop → derived `checked`.
  const checked = useMemo(() => {
    return decodeChecked(value[0], config) ?? config.initialChecked;
  }, [value, config]);

  // Emit the default state once after first render if no value arrived (no
  // URL state for this column). Deferred via setTimeout(0) so the parent's
  // URL-load useEffect — which fires *after* child effects (bottom-up) — has
  // a chance to populate `value` first. valueRef carries the freshest value
  // into the macrotask. Using empty deps + ref so we don't restart on every
  // value change.
  const didEmitDefault = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    if (didEmitDefault.current) return;
    const handle = setTimeout(() => {
      if (didEmitDefault.current) return;
      didEmitDefault.current = true;
      if (valueRef.current.length > 0) return;
      onChange(buildParams(config.initialChecked, config, info.ColumnName, paramType));
    }, 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Publish `checked` state to the per-grid sibling channel so identity
  // filters on the same grid (KioskFilter, LocationFilter) can react to
  // checkboxes like "Include Test Kiosks" without reading our InputParam
  // output (which is empty when onChecked=undefined).
  const filterTypeUC = (info.FilterType ?? "").toUpperCase();
  const eligibleSibling =
    filterTypeUC === "FILTERSHOWCHECKBOX" || filterTypeUC === "FILTERCHECKBOX";
  usePublishSiblingCheckbox(info.FilterText, checked, eligibleSibling);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.checked;
      // No local setChecked — the new param flows back through onChange,
      // updates `value`, and `checked` derives. Controlled component.
      onChange(buildParams(next, config, info.ColumnName, paramType));
    },
    [config, info.ColumnName, paramType, onChange],
  );

  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--space-1)",
        fontSize: "var(--text-sm)",
        color: "var(--color-text)",
        cursor: "pointer",
        padding: "2px 4px",
      }}
    >
      <input type="checkbox" checked={checked} onChange={handleChange} />
      {info.FilterText || info.Header || info.ColumnName}
    </label>
  );
}
